import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowUp, ArrowDown, TrendingUp, Users, Receipt, Check, Circle, AlertTriangle, ArrowRight } from "@/components/icons";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Line, ComposedChart, CartesianGrid, LabelList as LabelListFaturamento, ReferenceArea, ReferenceLine } from "recharts";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, ErrorState } from "@/components/common";
import Dica from "@/components/Dica";
import HelpTip from "@/components/HelpTip";
import { Info as InfoIcon } from "@/components/icons";
import { DICAS } from "@/lib/dicas";

// Painel Faturamento (redesign premium — mockup do usuário).
// Toda a métrica vem do endpoint `/sankhya/faturamento/painel`.
// A tela é um render fino — cálculos ficam no backend.

const BRAND_NAVY = "#003E62";
const BRAND_TEAL = "#1B8FB0";

function brl(v) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v || 0));
}
function brlShort(v) {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(2).replace(".", ",")} mi`;
  if (Math.abs(n) >= 1_000) return `R$ ${Math.round(n / 1_000)} mil`;
  return brl(n);
}
function iniciais(nome) {
  const s = (nome || "").trim().split(/\s+/);
  return ((s[0]?.[0] || "") + (s[1]?.[0] || "")).toUpperCase() || "?";
}
function fmtDataCurta(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleDateString("pt-BR");
}
function fmtHora(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

const PERIODOS = [
  { k: "mes_atual", l: "Mês atual" },
  { k: "mes_anterior", l: "Mês anterior" },
  { k: "trimestre", l: "Trimestre" },
  { k: "ano", l: "Ano" },
];

export default function Faturamento() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const [periodo, setPeriodo] = useState(() => sp.get("periodo") || "mes_atual");
  const [dados, setDados] = useState(null);
  const [hier, setHier] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  // PAINEL-1.4 (item 3) — cota real do escopo para rescaler a linha do ritmo.
  const [cotaRealEscopo, setCotaRealEscopo] = useState(null);
  const [aba, setAba] = useState("__equipe__"); // aba de Maiores clientes
  // LOTE FAT-1 — Faturamento por nível (DG apenas). Drill-down Diretor → Gerente → Vendedores.
  const [fatNivel, setFatNivel] = useState("diretores"); // "diretores"|"gerentes"|"vendedores"
  const [drillDirId, setDrillDirId] = useState(null);
  const [drillGerId, setDrillGerId] = useState(null);

  const load = async () => {
    setCarregando(true); setErro(null);
    try {
      const isDG = user?.role === "diretor_geral";
      const [painel, hierResp] = await Promise.all([
        api.get(`/sankhya/faturamento/painel?periodo=${periodo}`),
        isDG ? api.get("/hierarquia").catch(() => ({ data: null })) : Promise.resolve({ data: null }),
      ]);
      setDados(painel.data);
      setHier(hierResp.data);
    } catch (e) { setErro(errMsg(e)); } finally { setCarregando(false); }
  };
  useEffect(() => { load(); }, [periodo]);
  // PAINEL-1.4 (item 3) — busca a cota real do mês para o escopo do usuário.
  // Vendedor: cota individual; gestor: cota real (cota_real_helper no backend).
  useEffect(() => {
    const hoje = new Date();
    let ano = hoje.getFullYear();
    let mes = hoje.getMonth() + 1;
    if (periodo === "mes_anterior") {
      mes -= 1;
      if (mes < 1) { mes = 12; ano -= 1; }
    } else if (periodo !== "mes_atual") {
      setCotaRealEscopo(null);
      return;
    }
    api.get(`/paineis/lideranca/cota-real-mes?ano=${ano}&mes=${mes}`)
      .then((r) => setCotaRealEscopo(r.data?.cota_real || null))
      .catch(() => setCotaRealEscopo(null));
  }, [periodo]);
  // Reset drill quando trocar o período.
  useEffect(() => { setDrillDirId(null); setDrillGerId(null); setFatNivel("diretores"); }, [periodo]);

  // LOTE FAT-1 — Dados agrupados por nível (SÓ DG). Fonte: `por_pessoa` do painel
  // cruzado com `/api/hierarquia`. Zero cálculo novo de faturamento, zero write.
  // FAT-1.1 (item 4): meta da aba Gerentes/Diretores usa a META DA EQUIPE —
  // prioridade para a cota cadastrada do próprio gestor (`gerSelf.meta` /
  // `dirSelf.meta`), que já reflete o total da estrutura. Só cai no fallback
  // "soma dos subordinados" quando a cota do gestor é zero/null, para evitar
  // dupla contagem (ex.: Gustavo Januzi: gerSelf.meta=1.350.000 vs
  // soma vendedores=147.867 → usa só a cota do gerente, 18,8%).
  const nivelData = useMemo(() => {
    if (user?.role !== "diretor_geral" || !hier || !dados?.por_pessoa) return null;
    const por = dados.por_pessoa || [];
    const totalEmpresa = Number(dados.totais?.liquido || 0);
    const byId = new Map(por.map((p) => [p.user_id, p]));
    const dirs = hier.diretores || [];
    const diretores = dirs.map((d) => {
      const dirSelf = byId.get(d.id) || null;
      let totalEstrutura = dirSelf ? Number(dirSelf.faturamento || 0) : 0;
      const gerentes = (d.gerentes || []).map((g) => {
        const gerSelf = g.id ? byId.get(g.id) : null;
        let totalEquipe = gerSelf ? Number(gerSelf.faturamento || 0) : 0;
        const vendedores = (g.vendedores || []).map((v) => {
          const vp = byId.get(v.id);
          const total = vp ? Number(vp.faturamento || 0) : 0;
          totalEquipe += total;
          return {
            id: v.id, nome: v.name || vp?.nome || "", total,
            meta: vp?.meta ?? null, pct: vp?.meta_atingido_pct ?? null, temMeta: !!vp?.tem_meta,
            pctTotal: vp?.participacao_pct ?? null,
            gerenteNome: g.name || "",
          };
        });
        // LOTE R-3a (item 1): somar o `totalEquipe` COMPLETO do gerente (que
        // já inclui a venda própria do gerente + vendedores dele), e no caso
        // de "vendedores diretos" ao diretor (`g.id === null`) também somar
        // a soma dos vendedores diretos. Antes, somávamos apenas a soma dos
        // vendedores, o que deixava a VENDA PRÓPRIA do gerente de fora da
        // estrutura do diretor e empurrava esses valores para "Sem vínculo".
        totalEstrutura += totalEquipe;
        // FAT-1.1: meta da equipe — cota cadastrada do gerente tem precedência.
        const gerMetaPropria = Number(gerSelf?.meta || 0);
        const somaVendedores = vendedores.reduce((a, v) => a + Number(v.meta || 0), 0);
        const metaEquipe = gerMetaPropria > 0 ? gerMetaPropria : somaVendedores;
        const temMetaEquipe = metaEquipe > 0;
        const pctEquipe = temMetaEquipe ? (totalEquipe / metaEquipe) * 100 : null;
        return {
          id: g.id, nome: g.name || gerSelf?.nome || "",
          vendaPropria: gerSelf ? Number(gerSelf.faturamento || 0) : 0,
          totalEquipe,
          meta: temMetaEquipe ? metaEquipe : null,
          pct: pctEquipe,
          temMeta: temMetaEquipe,
          diretorId: d.id, diretorNome: d.name || "",
          vendedores,
        };
      });
      // FAT-1.1: meta da estrutura do diretor — cota cadastrada do diretor
      // tem precedência; caindo nela, soma as metas de equipe de cada
      // gerente abaixo (que, por sua vez, já evitaram dupla contagem).
      const dirMetaPropria = Number(dirSelf?.meta || 0);
      const somaMetasEquipes = gerentes.reduce((a, g) => a + Number(g.meta || 0), 0);
      const metaEstrutura = dirMetaPropria > 0 ? dirMetaPropria : somaMetasEquipes;
      const temMetaEstrutura = metaEstrutura > 0;
      const pctEstrutura = temMetaEstrutura ? (totalEstrutura / metaEstrutura) * 100 : null;
      return {
        id: d.id, nome: d.name || dirSelf?.nome || "",
        vendaPropria: dirSelf ? Number(dirSelf.faturamento || 0) : 0,
        totalEstrutura,
        meta: temMetaEstrutura ? metaEstrutura : null,
        pct: pctEstrutura,
        temMeta: temMetaEstrutura,
        gerentes,
      };
    });
    const somaDir = diretores.reduce((a, d) => a + d.totalEstrutura, 0);
    const somaGer = diretores.reduce((a, d) => a + d.gerentes.reduce((b, g) => b + g.totalEquipe, 0), 0);
    const todosVendedoresRaw = diretores.flatMap((d) => d.gerentes.flatMap((g) => g.vendedores));
    // FAT-1.1: dedup por id (um vendedor pode aparecer sob > 1 diretor na
    // hierarquia; a aba Vendedores precisa ser lista única).
    const vendedoresPorId = new Map();
    for (const v of todosVendedoresRaw) {
      if (!v.id || vendedoresPorId.has(v.id)) continue;
      vendedoresPorId.set(v.id, v);
    }
    const todosVendedores = Array.from(vendedoresPorId.values());
    const somaVen = todosVendedores.reduce((a, v) => a + v.total, 0);
    return {
      diretores,
      todosVendedores: todosVendedores.slice().sort((a, b) => b.total - a.total),
      totalEmpresa,
      semVinculo: {
        diretores: Math.max(0, totalEmpresa - somaDir),
        gerentes:  Math.max(0, totalEmpresa - somaGer),
        vendedores: Math.max(0, totalEmpresa - somaVen),
      },
    };
  }, [user, hier, dados]);

  // Deep link ?user_id= veio de MembroDetalhe → redireciona pra tela cheia.
  useEffect(() => {
    const uid = sp.get("user_id");
    if (!uid || !dados) return;
    const p = (dados.por_pessoa || []).find((x) => x.user_id === uid);
    if (p) nav(`/faturamento/vendedor/${p.user_id}?periodo=${periodo}&nome=${encodeURIComponent(p.nome || "")}`, { replace: true });
  }, [dados, sp, periodo, nav]);

  const abrirPessoa = (p) => {
    // Navega para tela cheia, preservando o período selecionado (via URL) para
    // que o botão Voltar traga o usuário exatamente ao mesmo estado.
    nav(`/faturamento/vendedor/${p.user_id}?periodo=${periodo}&nome=${encodeURIComponent(p.nome || "")}`);
  };

  if (carregando && !dados) return <Loading />;
  if (erro) return <ErrorState onRetry={load} message={erro} />;
  if (!dados) return null;

  const t = dados.totais || {};
  const du = dados.dias_uteis || {};
  // PAINEL-1.5 — para gestor roles, substitui a meta exibida (soma dos
  // vendedores) pela COTA REAL do escopo (via /paineis/lideranca/cota-real-mes).
  // Vendedor continua com a cota individual, que já é o mesmo valor.
  const usarCotaReal = cotaRealEscopo != null && cotaRealEscopo > 0;
  const metaExibida = usarCotaReal ? cotaRealEscopo : t.meta;
  const atingidoPctExibido = usarCotaReal && t.liquido != null
    ? Math.round(((t.liquido || 0) / cotaRealEscopo) * 1000) / 10
    : t.atingido_pct;
  const temMeta = !!(usarCotaReal || (dados.tem_meta && t.meta));
  const metaLabel = usarCotaReal ? "Cota real" : "Meta da equipe";
  const metaInternaEquipeExibida = (usarCotaReal && t.meta && Math.abs(t.meta - cotaRealEscopo) > 1) ? t.meta : null;

  const abasVend = dados.maiores_clientes?.por_vendedor || [];
  const equipeTop = dados.maiores_clientes?.equipe || [];
  const abaAtual = aba === "__equipe__"
    ? { itens: equipeTop.slice(0, 6), total: equipeTop.length }
    : (() => {
        const v = abasVend.find((x) => x.user_id === aba);
        return { itens: (v?.itens || []).slice(0, 6), total: v?.itens?.length || 0 };
      })();

  const dirAtual = drillDirId ? (nivelData?.diretores || []).find((d) => d.id === drillDirId) : null;
  const gerAtual = drillGerId && dirAtual ? dirAtual.gerentes.find((g) => g.id === drillGerId) : null;

  return (
    <div className="pb-24 md:pb-6">
      {/* Cabeçalho */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-navy tracking-tight">Faturamento</h1>
          <div className="mt-1 flex items-center gap-2 flex-wrap text-xs">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-1 font-medium" data-testid="fat-sankhya-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Sankhya{dados.atualizado_em ? ` · atualizado às ${fmtHora(dados.atualizado_em)}` : " · aguardando 1ª sincronização"}
            </span>
            <span className="text-muted-foreground">{dados.escopo?.label}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-full border border-input bg-white p-1" data-testid="fat-periodo">
            {PERIODOS.map((p) => (
              <button
                key={p.k}
                onClick={() => setPeriodo(p.k)}
                className={`px-3 h-8 text-sm rounded-full transition-colors ${periodo === p.k ? "bg-navy text-white" : "text-slate-600 hover:text-navy"}`}
                data-testid={`fat-periodo-${p.k}`}
                style={periodo === p.k ? { background: BRAND_NAVY } : {}}
              >
                {p.l}
              </button>
            ))}
          </div>
          <div className="text-sm text-slate-600 tabular-nums" data-testid="fat-periodo-label">{dados.periodo?.label}</div>
        </div>
      </div>

      {/* Faixa principal */}
      <div className="rounded-2xl p-5 md:p-7 mb-6 grid gap-6 md:grid-cols-[2fr_1fr] shadow-[0_10px_40px_rgba(11,59,92,0.15)] min-w-0 overflow-hidden" style={{ background: `linear-gradient(135deg, ${BRAND_NAVY} 0%, #0F4A72 100%)` }} data-testid="fat-hero">
        <div className="text-white min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-white/70 inline-flex items-center gap-1">
            Faturamento líquido · {dados.periodo?.mes_label || dados.periodo?.label}
            <HelpTip label="Soma das notas fiscais do período menos devoluções (sankhya._liq_periodo). Fonte: Sankhya.">
              <button type="button" className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-white/80 hover:text-white" data-testid="fat-liquido-info" aria-label="O que significa este número?">
                <InfoIcon className="w-3.5 h-3.5" />
              </button>
            </HelpTip>
          </div>
          <div className="text-2xl sm:text-3xl md:text-5xl font-semibold mt-1 tabular-nums break-words" data-testid="fat-total">{brl(t.liquido)}</div>
          {t.variacao_pct != null && (
            <div className={`inline-flex items-center gap-1 mt-2 rounded-lg px-2 py-1 text-xs font-semibold ${t.variacao_pct >= 0 ? "bg-emerald-500/20 text-emerald-200" : "bg-rose-500/20 text-rose-200"}`} data-testid="fat-variacao">
              {t.variacao_pct >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
              {Math.abs(t.variacao_pct)}% vs. mesmo período do mês anterior
            </div>
          )}
          {temMeta && (
            <div className="mt-4">
              <div className="text-sm text-white/85 inline-flex items-center gap-1">
                {metaLabel} <span className="font-semibold tabular-nums">{brl(metaExibida)}</span> · <span className="font-semibold">{typeof atingidoPctExibido === "number" ? atingidoPctExibido.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : atingidoPctExibido}%</span> atingido
                <HelpTip label={usarCotaReal
                  ? "Cota real cadastrada do responsável no período (cotas_oficiais_gestor.meses). % atingido = Líquido ÷ Cota real × 100."
                  : "Cota do responsável (ou soma da equipe) no mês · % atingido = Líquido ÷ Meta × 100 (reports.aggregate_goal_revenue)."}>
                  <button type="button" className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-white/80 hover:text-white" data-testid="fat-meta-info" aria-label="O que significa este número?">
                    <InfoIcon className="w-3.5 h-3.5" />
                  </button>
                </HelpTip>
              </div>
              <div className="relative h-2.5 mt-2 rounded-full bg-white/20 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, atingidoPctExibido || 0)}%`, background: BRAND_TEAL }} />
                {du.ritmo_esperado_pct > 0 && du.ritmo_esperado_pct <= 100 && (
                  <Dica
                    id="fat-marcador-meta"
                    texto={DICAS.fatMarcadorMeta}
                    className="absolute top-0 bottom-0"
                    style={{ position: "absolute", left: `${du.ritmo_esperado_pct}%`, width: 2 }}
                  >
                    <div
                      className="h-full w-full bg-white/70"
                      data-testid="fat-marcador"
                      title={`Ritmo esperado: ${du.ritmo_esperado_pct}%`}
                    />
                  </Dica>
                )}
              </div>
              <div className="flex items-center justify-between text-[11px] text-white/70 mt-1.5">
                <span>0</span>
                <span data-testid="fat-ritmo">ritmo esperado para hoje: {du.ritmo_esperado_pct}% ({du.decorridos} de {du.total} dias úteis)</span>
                <span>100%</span>
              </div>
              {metaInternaEquipeExibida != null && (
                <div className="text-[11px] text-white/60 mt-1" data-testid="fat-meta-interna-equipe">
                  Meta interna da equipe: <span className="tabular-nums">{brl(metaInternaEquipeExibida)}</span>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Dica id="fat-clientes-dica" texto={DICAS.fatClientesFaturados} className="min-w-0">
          <MiniCard
            label="Clientes faturados"
            value={String(t.clientes_faturados || 0)}
            sub={`${t.clientes_novos || 0} novos no mês`}
            testId="fat-clientes"
          />
          </Dica>
          <Dica id="fat-ticket-dica" texto={DICAS.fatTicketMedio} className="min-w-0">
          <MiniCard
            label="Ticket médio por nota"
            value={brl(t.ticket_medio)}
            sub={`${t.notas_venda || 0} notas emitidas`}
            testId="fat-ticket"
          />
          </Dica>
        </div>
      </div>

      {/* FAT-1.1 — "Faturamento por nível" MOVIDO para o TOPO (apenas Diretor Geral),
       * logo depois do Hero. Antes estava no fim da página; passou a abrir a visão
       * imediatamente abaixo do card azul. */}
      {user?.role === "diretor_geral" && nivelData && (
        <section className="bg-white rounded-2xl border border-black/5 p-4 md:p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] mb-6" data-testid="fat-por-nivel">
          <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
            <div>
              <h2 className="text-base md:text-lg font-semibold text-navy">Faturamento por nível</h2>
              <div className="text-[11px] text-muted-foreground">Diretor, gerente, vendedor — mesma fonte do painel, agrupada pela hierarquia.</div>
            </div>
            <div className="inline-flex rounded-full border border-input bg-white p-1" data-testid="fat-nivel-tabs">
              {[["diretores","Diretores"],["gerentes","Gerentes"],["vendedores","Vendedores"]].map(([k,l]) => (
                <button key={k}
                  onClick={() => { setFatNivel(k); setDrillDirId(null); setDrillGerId(null); }}
                  className={`px-3 h-8 text-sm rounded-full ${fatNivel===k?"bg-navy text-white":"text-slate-600 hover:text-navy"}`}
                  style={fatNivel===k?{background:BRAND_NAVY}:{}}
                  data-testid={`fat-nivel-${k}`}
                >{l}</button>
              ))}
            </div>
          </div>

          {/* Breadcrumb do drill */}
          {(drillDirId || drillGerId) && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3" data-testid="fat-nivel-breadcrumb">
              <button onClick={() => { setDrillDirId(null); setDrillGerId(null); setFatNivel("diretores"); }}
                className="hover:text-navy font-medium" data-testid="fat-nivel-voltar-topo">← Voltar aos diretores</button>
              {dirAtual && <span>· {dirAtual.nome}</span>}
              {gerAtual && (<>
                <span>›</span>
                <button onClick={() => setDrillGerId(null)} className="hover:text-navy" data-testid="fat-nivel-voltar-gerente">
                  {gerAtual.nome}
                </button>
              </>)}
            </div>
          )}

          {/* Aba: Diretores (ou drill nível 1) */}
          {fatNivel === "diretores" && !drillDirId && (
            <NivelTabela
              rows={nivelData.diretores.map((d) => ({
                id: d.id, nome: d.nome, sub: "Diretor(a)",
                total: d.totalEstrutura, extra: d.vendaPropria,
                meta: d.meta, pct: d.pct, temMeta: d.temMeta,
                onClick: () => { setDrillDirId(d.id); setDrillGerId(null); setFatNivel("gerentes"); },
                testId: `fat-dir-${d.id}`,
              }))}
              totalLabel="Total da estrutura"
              extraLabel="Venda própria"
              totalEmpresa={nivelData.totalEmpresa}
              semVinculo={nivelData.semVinculo.diretores}
            />
          )}

          {/* Aba: Gerentes (ou drill nível 1 vindo de Diretores) */}
          {fatNivel === "gerentes" && !drillGerId && (
            <NivelTabela
              rows={(drillDirId
                ? (dirAtual?.gerentes || [])
                : nivelData.diretores.flatMap((d) => d.gerentes)
              ).filter((g) => g.id || g.totalEquipe > 0).map((g) => ({
                id: g.id || `vdir-${g.diretorId}`,
                nome: g.id ? g.nome : `Vendedores diretos — ${g.diretorNome}`,
                sub: g.id ? "Gerente" : "Vinculados ao diretor",
                total: g.totalEquipe, extra: g.vendaPropria,
                meta: g.meta, pct: g.pct, temMeta: g.temMeta,
                onClick: g.id ? () => { setDrillGerId(g.id); setFatNivel("vendedores"); } : null,
                testId: `fat-ger-${g.id || "vdir-"+g.diretorId}`,
              }))}
              totalLabel="Total da equipe"
              extraLabel="Venda própria"
              totalEmpresa={nivelData.totalEmpresa}
              semVinculo={drillDirId ? null : nivelData.semVinculo.gerentes}
            />
          )}

          {/* Aba: Vendedores (FAT-1.1: ganha coluna "% do total") */}
          {fatNivel === "vendedores" && (
            <NivelTabela
              rows={(gerAtual
                ? gerAtual.vendedores
                : nivelData.todosVendedores
              ).sort((a,b) => b.total - a.total).map((v) => ({
                id: v.id, nome: v.nome,
                sub: v.gerenteNome ? `Gerente: ${v.gerenteNome}` : "Vendedor(a)",
                total: v.total, extra: null,
                meta: v.meta, pct: v.pct, temMeta: v.temMeta,
                pctTotal: v.pctTotal,
                onClick: null,
                testId: `fat-ven-${v.id}`,
              }))}
              totalLabel="Total de vendas"
              extraLabel={null}
              totalEmpresa={nivelData.totalEmpresa}
              semVinculo={gerAtual ? null : nivelData.semVinculo.vendedores}
              showPctTotal
            />
          )}

          {/* FAT-1 (item 3) / PAINEL-1.8 (item 6):
           *  - Diretores: "Vendas diretas MPires" com texto genérico sobre
           *    atendimento interno/televendas.
           *  - Gerentes: mesma label, mas texto específico e breakdown por
           *    diretor (venda própria do próprio diretor sem gerente).
           *  - Vendedores: idem Diretores.
           *  - Em TODAS as abas: lista "Usuários sem código de vendedor do
           *    Sankhya" aparece em bloco SEPARADO (não é venda em R$). */}
          {(nivelData.semVinculo[fatNivel] || 0) > 0 && (
            <div className="mt-4 pt-4 border-t border-black/5 text-xs" data-testid="fat-por-nivel-sem-vinculo">
              <div className="flex items-center gap-1.5 flex-wrap text-amber-800 font-medium">
                <span>Vendas diretas MPires: <span className="tabular-nums">{brl(nivelData.semVinculo[fatNivel])}</span></span>
                {fatNivel === "gerentes" ? (
                  <HelpTip label={`Vendas feitas nos códigos de vendedor dos próprios diretores (${nivelData.diretores.map((d) => d.nome.split(" ")[0]).join(", ")}), que não pertencem a nenhum gerente.`}>
                    <button type="button" className="inline-flex items-center justify-center w-4 h-4 rounded-full text-amber-700 hover:text-amber-900" data-testid="fat-sem-vinculo-info" aria-label="O que significa este número?">
                      <InfoIcon className="w-3.5 h-3.5" />
                    </button>
                  </HelpTip>
                ) : (
                  <HelpTip label="Vendas feitas diretamente pela MPires (atendimento interno e televendas), sem vendedor da equipe comercial. Entram no total da empresa e não contam na cota de nenhum diretor, gerente ou vendedor.">
                    <button type="button" className="inline-flex items-center justify-center w-4 h-4 rounded-full text-amber-700 hover:text-amber-900" data-testid="fat-sem-vinculo-info" aria-label="O que significa este número?">
                      <InfoIcon className="w-3.5 h-3.5" />
                    </button>
                  </HelpTip>
                )}
              </div>
              {fatNivel === "gerentes" && (
                <div className="text-slate-600 mt-1 font-normal flex flex-wrap gap-x-3 gap-y-0.5">
                  {nivelData.diretores.filter((d) => (d.vendaPropria || 0) > 0).map((d) => (
                    <span key={d.id}>{d.nome.split(" ")[0]}: <span className="tabular-nums text-slate-700">{brl(d.vendaPropria)}</span></span>
                  ))}
                </div>
              )}
            </div>
          )}
          {dados.sem_vinculo?.length > 0 && (
            <div className="mt-3 text-xs" data-testid="fat-por-nivel-sem-codvend">
              <div className="flex items-center gap-1.5 flex-wrap text-slate-700 font-medium">
                <span>Usuários sem código de vendedor do Sankhya:</span>
                <HelpTip label="Estas pessoas têm acesso ao CRM, mas nenhuma venda do Sankhya está ligada a elas. Se vendem, o admin precisa ligar o código de vendedor no cadastro.">
                  <button type="button" className="inline-flex items-center justify-center w-4 h-4 rounded-full text-slate-500 hover:text-slate-700" data-testid="fat-sem-codvend-info" aria-label="O que significa esta lista?">
                    <InfoIcon className="w-3.5 h-3.5" />
                  </button>
                </HelpTip>
              </div>
              <div className="text-slate-600 mt-1 font-normal">
                {dados.sem_vinculo.map((s) => s.nome).join(", ")}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Gráfico + Maiores clientes */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-6 mb-6">
        <section
          className="min-w-0 relative rounded-[24px] overflow-hidden p-5 md:p-7"
          style={{
            background: "linear-gradient(135deg, #FFFFFF 0%, #E3EFF5 100%)",
            border: "1px solid #D7E6EE",
            boxShadow: "0 8px 24px rgba(11,59,92,0.06)",
          }}
          data-testid="fat-chart-card"
        >
          {/* Círculos decorativos (prévia) */}
          <div aria-hidden className="pointer-events-none absolute -right-10 -top-16 w-64 h-64 rounded-full" style={{ background: "#D7E6EE", opacity: 0.6 }} />
          <div aria-hidden className="pointer-events-none absolute -right-24 top-4 w-40 h-40 rounded-full" style={{ background: "#B9DCE8", opacity: 0.5 }} />

          <div className="relative">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: TEAL_SOFT }} data-testid="fat-chart-eyebrow">
              FATURAMENTO SANKHYA · {(dados.periodo?.mes_label || dados.periodo?.label || "").toUpperCase()} · {(dados.escopo?.label || "").toUpperCase()}
            </div>
            <h3 className="font-extrabold text-xl md:text-2xl mt-1" style={{ color: NAVY_SOFT, fontFamily: 'Barlow, system-ui, sans-serif' }}>
              Acumulado do mês × ritmo da cota
            </h3>
            <div className="mt-1 flex items-center gap-4 flex-wrap text-[12px]" style={{ color: "#4A6478" }} data-testid="fat-chart-legenda">
              <span className="inline-flex items-center gap-1.5"><span className="inline-block w-4 h-1 rounded" style={{ background: NAVY_SOFT }} /> Faturado acumulado</span>
              {temMeta ? (
                <span className="inline-flex items-center gap-1.5"><span className="inline-block w-4 border-t-2 border-dashed" style={{ borderColor: DOURADO }} /> Ritmo da cota (dias úteis)</span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-slate-400"><span className="inline-block w-4 border-t-2 border-dashed border-slate-300" /> Ritmo da cota: cota não cadastrada</span>
              )}
              <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: "#D7E6EE" }} /> Fim de semana / feriado</span>
            </div>

            {/* 4 blocos de números */}
            {/* eslint-disable react/no-unstable-nested-components */}
            {(() => {
              const projecaoPctCota = usarCotaReal && t.projecao != null
                ? Math.round((t.projecao / cotaRealEscopo) * 1000) / 10
                : t.projecao_pct_meta;
              const faltaCota = usarCotaReal ? Math.max(0, cotaRealEscopo - (t.liquido || 0)) : t.falta_meta;
              const metaBatida = usarCotaReal ? (t.liquido || 0) >= cotaRealEscopo : t.meta_batida;
              const diasRestantes = du.restantes || 0;
              const esperadoHoje = (metaExibida && du.ritmo_esperado_pct)
                ? (Number(metaExibida) * (du.ritmo_esperado_pct / 100))
                : null;
              const Block = ({ label, value, sub, testId }) => (
                <div className="rounded-xl p-3 md:p-4 border" style={{ background: "#FFFFFF", borderColor: "#D7E6EE" }} data-testid={testId}>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: TEAL_SOFT }}>{label}</div>
                  <div className="font-extrabold text-xl md:text-2xl mt-1" style={{ color: NAVY_SOFT, fontFamily: 'Barlow, system-ui, sans-serif' }}>{value}</div>
                  {sub && <div className="text-[11px] mt-1" style={{ color: "#4A6478" }}>{sub}</div>}
                </div>
              );
              return (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4" data-testid="fat-chart-blocos">
                  <Block
                    label="Faturado no mês"
                    value={brlMi(t.liquido)}
                    sub={dados.atualizado_em ? `até ${new Date(dados.atualizado_em).toLocaleDateString("pt-BR")}` : null}
                    testId="fat-bloco-faturado"
                  />
                  <Block
                    label="Ritmo da cota"
                    value={temMeta ? `${du.ritmo_esperado_pct || 0}%` : "—"}
                    sub={temMeta && esperadoHoje != null ? `${brlMi(esperadoHoje)} esperado hoje` : "cota não cadastrada"}
                    testId="fat-bloco-ritmo"
                  />
                  <Block
                    label="Projeção do mês"
                    value={brlMi(t.projecao)}
                    sub={temMeta ? `${projecaoPctCota || 0}% da ${usarCotaReal ? "cota" : "meta"}` : "no ritmo atual"}
                    testId="fat-bloco-projecao"
                  />
                  <Block
                    label={usarCotaReal ? "Falta para a cota" : "Falta para a meta"}
                    value={!temMeta ? "—" : metaBatida ? (usarCotaReal ? "Cota batida" : "Meta batida") : brlMi(faltaCota)}
                    sub={!temMeta ? "—" : metaBatida ? "" : `${diasRestantes} ${diasRestantes === 1 ? "dia útil restante" : "dias úteis restantes"}`}
                    testId="fat-bloco-falta"
                  />
                </div>
              );
            })()}
            {/* eslint-enable react/no-unstable-nested-components */}

            {/* Gráfico */}
            <div className="mt-4 h-[300px] md:h-[460px]">
              <ChartAcumulado
                dados={dados.acumulado_diario || []}
                temMeta={temMeta}
                metaOriginal={dados?.totais?.meta}
                cotaReal={cotaRealEscopo}
              />
            </div>
          </div>
        </section>

        <section className="min-w-0 rounded-2xl bg-white border border-black/5 p-5 md:p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid="fat-maiores-card">
          <div className="flex items-center justify-between mb-3">
            <Dica id="fat-maiores-dica" texto={DICAS.fatMaioresClientes}>
              <h3 className="text-sm font-semibold text-navy">Maiores clientes</h3>
            </Dica>
            <div className="text-[11px] text-muted-foreground">top 10 · mostrando {abaAtual.itens.length}</div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap mb-3">
            <TopTab active={aba === "__equipe__"} onClick={() => setAba("__equipe__")} testId="fat-top-tab-equipe">Equipe</TopTab>
            {abasVend.slice(0, 3).map((v) => (
              <TopTab key={v.user_id} active={aba === v.user_id} onClick={() => setAba(v.user_id)} testId={`fat-top-tab-${v.user_id}`}>{firstName(v.nome)}</TopTab>
            ))}
            {abasVend.length > 3 && (
              <select
                onChange={(e) => e.target.value && setAba(e.target.value)}
                value={abasVend.slice(3).some((v) => v.user_id === aba) ? aba : ""}
                className="h-8 px-2 text-xs rounded-full border border-input bg-white text-slate-600"
                data-testid="fat-top-vendedor-select"
              >
                <option value="">+{abasVend.length - 3}</option>
                {abasVend.slice(3).map((v) => (
                  <option key={v.user_id} value={v.user_id}>{v.nome}</option>
                ))}
              </select>
            )}
          </div>
          <div className="space-y-2">
            {abaAtual.itens.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">Sem clientes no período.</div>}
            {abaAtual.itens.map((c, i) => {
              const max = abaAtual.itens[0]?.liquido || 1;
              const codLabel = (c.codparcs || []).slice(0, 3).join(" + ") + ((c.codparcs || []).length > 3 ? "…" : "");
              return (
                <button
                  key={`${c.cgc_raiz}-${i}`}
                  onClick={() => nav(`/clientes?codparc=${(c.codparcs || [])[0] || ""}`)}
                  className="w-full text-left flex items-center gap-3 py-1.5 group hover:bg-slate-50 rounded-lg px-2 -mx-2"
                  data-testid={`fat-maior-cli-${i}`}
                >
                  <span className="w-5 text-xs text-muted-foreground tabular-nums">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-navy truncate">{c.nome || "—"}</div>
                    <div className="text-[11px] text-muted-foreground">{c.vendedor || "—"}{codLabel ? ` · ${codLabel}` : ""}</div>
                    <div className="mt-1 h-1 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${(c.liquido / max) * 100}%`, background: BRAND_TEAL }} />
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-navy tabular-nums">{brl(c.liquido)}</div>
                </button>
              );
            })}
          </div>
        </section>
      </div>

      {/* FAT-1.1 (item 2): "Por pessoa" NÃO aparece para Diretor Geral — a
       * aba "Vendedores" do card "Faturamento por nível" já traz essa lista
       * com a coluna "% do total". Nas visões de Vendedor/Gerente/Diretor
       * nada muda. */}
      {user?.role !== "diretor_geral" && (
      <section className="rounded-2xl bg-white border border-black/5 p-5 md:p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid="fat-pessoa-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-sm font-semibold text-navy">Por pessoa</h3>
          <span className="text-[11px] text-muted-foreground">participação no faturamento e meta individual</span>
        </div>
        {dados.por_pessoa.length === 0 && (
          <div className="text-sm text-muted-foreground py-6 text-center">Ninguém com vínculo com o Sankhya no escopo.</div>
        )}
        <div className="space-y-1.5">
          {dados.por_pessoa.map((p, i) => (
            <button
              key={p.user_id}
              onClick={() => abrirPessoa(p)}
              className="w-full grid grid-cols-[20px_32px_1fr_auto] sm:grid-cols-[24px_36px_1fr_1fr_auto_86px] gap-2 sm:gap-3 items-center px-2 py-2.5 hover:bg-slate-50 rounded-lg group text-left min-w-0"
              data-testid={`fat-pessoa-row-${p.user_id}`}
            >
              <span className="text-xs text-muted-foreground tabular-nums">{i + 1}</span>
              <span className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-xs font-semibold text-white shrink-0" style={{ background: BRAND_TEAL }}>
                {iniciais(p.nome)}
              </span>
              <div className="min-w-0">
                <div className="text-sm font-medium text-navy truncate">{p.nome}</div>
                <div className="text-[11px] text-muted-foreground truncate">{p.role_label}</div>
                <div className="sm:hidden mt-1.5 space-y-1">
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${Math.min(100, p.participacao_pct || 0)}%`, background: BRAND_TEAL }} />
                  </div>
                  <div className="flex items-center gap-2 text-[11px] tabular-nums">
                    <span className="text-muted-foreground">{p.participacao_pct || 0}%</span>
                    <span className="font-semibold text-navy">{brl(p.faturamento)}</span>
                    <span className="ml-auto"><MetaBadge tem={p.tem_meta} pct={p.meta_atingido_pct} /></span>
                  </div>
                </div>
              </div>
              <div className="hidden sm:block min-w-0">
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${Math.min(100, p.participacao_pct || 0)}%`, background: BRAND_TEAL }} />
                </div>
                <div className="text-[11px] text-muted-foreground mt-1 tabular-nums">{p.participacao_pct || 0}% do total</div>
              </div>
              <div className="hidden sm:block text-sm font-semibold text-navy text-right tabular-nums">{brl(p.faturamento)}</div>
              <div className="hidden sm:block text-right"><MetaBadge tem={p.tem_meta} pct={p.meta_atingido_pct} /></div>
              <ArrowRight className="sm:hidden w-4 h-4 text-slate-300 shrink-0" />
            </button>
          ))}
        </div>
        {dados.sem_vinculo?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-black/5 flex items-center justify-between flex-wrap gap-2 text-xs">
            <div className="text-slate-700 font-medium" data-testid="fat-sem-vinculo">
              Usuários sem código de vendedor do Sankhya · <span className="font-normal text-slate-600">{dados.sem_vinculo.map((s) => s.nome).join(", ")}</span>
            </div>
            {user?.role === "admin" && (
              <button onClick={() => nav("/ajustes/sankhya")} className="text-navy font-medium hover:underline inline-flex items-center gap-1" data-testid="fat-vincular-link">
                Vincular CODVEND <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
      </section>
      )}

      {/* Tela cheia dedicada — /faturamento/vendedor/:id substituiu o pop-up. */}
    </div>
  );
}

function MiniCard({ label, value, sub, tone, testId }) {
  const toneCls = tone === "green" ? "bg-emerald-500/20 border-emerald-400/30"
                : tone === "amber" ? "bg-amber-500/20 border-amber-400/30"
                : "bg-white/10 border-white/15";
  return (
    <div className={`rounded-xl p-3 border ${toneCls} text-white`} data-testid={testId}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-white/70">{label}</div>
      <div className="text-lg md:text-xl font-semibold mt-0.5 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-white/70 mt-0.5">{sub}</div>}
    </div>
  );
}

function TopTab({ active, onClick, testId, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`h-8 px-3 text-xs font-medium rounded-full transition-colors ${active ? "text-white" : "text-slate-600 hover:text-navy border border-input"}`}
      style={active ? { background: BRAND_NAVY } : {}}
    >
      {children}
    </button>
  );
}

function MetaBadge({ tem, pct }) {
  if (!tem) return <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 text-slate-500 text-[11px] font-medium px-2 py-1" data-testid="meta-badge-sem">sem meta</span>;
  if (pct >= 100) return <span className="inline-flex items-center gap-1 rounded-md bg-emerald-100 text-emerald-700 text-[11px] font-semibold px-2 py-1 tabular-nums"><Check className="w-3 h-3" />{Math.round(pct)}%</span>;
  if (pct >= 70) return <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 text-amber-700 text-[11px] font-semibold px-2 py-1 tabular-nums"><Circle className="w-3 h-3 fill-amber-400 text-amber-500" />{Math.round(pct)}%</span>;
  return <span className="inline-flex items-center gap-1 rounded-md bg-rose-100 text-rose-700 text-[11px] font-semibold px-2 py-1 tabular-nums"><AlertTriangle className="w-3 h-3" />{Math.round(pct)}%</span>;
}

function firstName(s) { return (s || "").trim().split(/\s+/)[0] || ""; }

/* eslint-disable react/no-unstable-nested-components */
// FAT-1 — Visual novo do cartão "Acumulado do mês × ritmo da cota".
// Mantém os MESMOS dados do backend (sankhya.py — zona congelada), mas:
//   - fundo claro em degradê branco → #E3EFF5 com círculos translúcidos;
//   - 4 blocos de números dentro do cartão (faturado · ritmo · projeção · falta);
//   - linha azul-marinho #006588 → #003E62, área em degradê;
//   - faixas verticais sutis em fins de semana (detectadas pelo dia da semana)
//     e feriados (detectados por "ritmo_meta" que não avançou quando há cota);
//   - linha pontilhada "HOJE", ponto branco com anel + seta e valor na ponta;
//   - tooltip azul-marinho (#003E62) arredondado;
//   - linha dourada tracejada (#C98A12) do ritmo com rótulo "Cota R$ X" no fim.
const DOURADO = "#C98A12";
const NAVY_SOFT = "#003E62";
const TEAL_SOFT = "#008BB0";

function brlMi(v) {
  // Formato "R$ 4,36 mi" / "R$ 240 mil" / "R$ 820".
  if (v == null) return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `R$ ${(n / 1_000_000).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} mi`;
  if (abs >= 1_000) return `R$ ${Math.round(n / 1_000).toLocaleString("pt-BR")} mil`;
  return `R$ ${Math.round(n).toLocaleString("pt-BR")}`;
}

function ChartAcumulado({ dados, temMeta, metaOriginal, cotaReal }) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);
  useEffect(() => {
    const onR = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  const fatorCota = (metaOriginal && cotaReal && metaOriginal > 0)
    ? Number(cotaReal) / Number(metaOriginal) : 1;
  const data = useMemo(() => (dados || []).map((d) => {
    const dt = d.iso ? new Date(d.iso + "T12:00:00") : null;
    const dow = dt ? dt.getDay() : 0; // 0=dom, 6=sab
    return {
      iso: d.iso,
      diaNum: dt ? dt.getDate() : 0,
      dow,
      label: dt ? String(dt.getDate()).padStart(2, "0") + "/" + String(dt.getMonth() + 1).padStart(2, "0") : "",
      acumulado: d.acumulado == null ? null : (d.acumulado || 0),
      ritmo_meta: d.ritmo_meta == null ? null : (d.ritmo_meta * fatorCota),
      ritmo_meta_raw: d.ritmo_meta,
      valor_dia: d.valor_dia == null ? null : (d.valor_dia || 0),
    };
  }), [dados, fatorCota]);
  const ultimoIdx = useMemo(() => {
    for (let i = data.length - 1; i >= 0; i -= 1) if (data[i].acumulado != null) return i;
    return -1;
  }, [data]);
  const faixasNaoUteis = useMemo(() => {
    const nao = data.map((d, i) => {
      const fds = d.dow === 0 || d.dow === 6;
      let feriado = false;
      if (temMeta && i > 0 && d.ritmo_meta_raw != null && data[i - 1].ritmo_meta_raw != null) {
        if (!fds && d.ritmo_meta_raw === data[i - 1].ritmo_meta_raw) feriado = true;
      }
      return fds || feriado;
    });
    const faixas = [];
    let i = 0;
    while (i < nao.length) {
      if (nao[i]) {
        let j = i;
        while (j + 1 < nao.length && nao[j + 1]) j += 1;
        faixas.push({ ini: data[i]?.label, fim: data[j]?.label });
        i = j + 1;
      } else {
        i += 1;
      }
    }
    return faixas;
  }, [data, temMeta]);

  const hojeLabel = ultimoIdx >= 0 ? data[ultimoIdx].label : null;
  // UI-1 (item 7) — ticks arredondados do eixo Y em "mi" com vírgula, sem repetir.
  const maxV = data.reduce((m, d) => Math.max(m, d.acumulado || 0, d.ritmo_meta || 0), 0);
  const step = maxV >= 2_000_000 ? 500_000 : maxV >= 1_000_000 ? 250_000 : maxV >= 500_000 ? 125_000 : 50_000;
  const topoY = Math.ceil((maxV * 1.1) / step) * step;
  const yTicks = [];
  for (let v = 0; v <= topoY; v += step) yTicks.push(v);
  const fmtYMi = (v) => {
    if (v === 0) return "0";
    return `${(v / 1_000_000).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 2 })} mi`;
  };
  const Tip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    const dt = p.iso ? new Date(p.iso + "T12:00:00") : null;
    const dow = dt ? ["domingo","segunda","terça","quarta","quinta","sexta","sábado"][dt.getDay()] : "";
    return (
      <div className="rounded-xl px-3 py-2 shadow-lg" style={{ background: NAVY_SOFT, color: "#fff" }}>
        <div className="font-semibold text-sm">{label} · {dow}</div>
        {p.valor_dia != null && <div className="text-xs mt-1">Dia: <b>{brlMi(p.valor_dia)}</b></div>}
        {p.acumulado != null && <div className="text-xs">Acumulado: <b>{brlMi(p.acumulado)}</b></div>}
        {p.ritmo_meta != null && <div className="text-xs">Ritmo da cota: <b style={{ color: "#F3C65A" }}>{brlMi(p.ritmo_meta)}</b></div>}
      </div>
    );
  };
  // UI-1 (item 7) — no celular, só o ponto na ponta (sem texto / sem seta); no
  // computador, mantém a seta e o valor como antes.
  const PontaHoje = (props) => {
    const { x, y, index, value } = props || {};
    if (index !== ultimoIdx || value == null) return null;
    if (isMobile) {
      return <circle cx={x} cy={y} r={5} fill="#fff" stroke={NAVY_SOFT} strokeWidth={3} />;
    }
    return (
      <g>
        <circle cx={x} cy={y} r={5} fill="#fff" stroke={NAVY_SOFT} strokeWidth={3} />
        <text x={x + 10} y={y - 18} fill={NAVY_SOFT} fontSize={16} fontWeight={800}>↗</text>
        <text x={x + 26} y={y - 12} fill={NAVY_SOFT} fontSize={16} fontWeight={800}>{brlMi(value)}</text>
        <text x={x + 26} y={y + 6} fill="#4A6478" fontSize={11}>até {hojeLabel}</text>
      </g>
    );
  };
  // UI-1 (item 7) — "Cota R$ X" vai para a legenda no celular, não desenha
  // mais no fim da linha. No computador mantém o rótulo.
  const RotuloCota = (props) => {
    const { x, y, index, value } = props || {};
    if (isMobile || index !== data.length - 1 || value == null) return null;
    return (
      <g>
        <text x={x + 6} y={y - 2} fill={DOURADO} fontSize={11} fontWeight={700}>Cota</text>
        <text x={x + 6} y={y + 12} fill={DOURADO} fontSize={12} fontWeight={700}>{brlMi(value)}</text>
      </g>
    );
  };
  const xTicks = useMemo(() => {
    const nums = new Set([1, 5, 10, 15, 20, 25]);
    if (data.length) nums.add(data[data.length - 1].diaNum);
    return data.filter((d) => nums.has(d.diaNum)).map((d) => d.label);
  }, [data]);
  if (data.length === 0) {
    return <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Sem dados no período.</div>;
  }
  // UI-1 (item 7) — no celular, margem direita pequena (gráfico ocupa a
  // largura toda do cartão); no computador, margem para os rótulos "HOJE/Cota".
  const marginRight = isMobile ? 14 : 110;
  const cotaMensal = (temMeta && data.length) ? data[data.length - 1]?.ritmo_meta : null;
  return (
    <div className="h-full flex flex-col">
      {/* Legenda de apoio só no celular — mantém "Cota R$ X" visível. */}
      {isMobile && (
        <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-[11px] text-slate-600 px-1 pb-1" data-testid="fat-chart-legenda-mobile">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-5 border-t-[3px]" style={{ borderColor: NAVY_SOFT }} /> acumulado
          </span>
          {temMeta && (
            <span className="inline-flex items-center gap-1.5">
              <span className="w-5 border-t-2 border-dashed" style={{ borderColor: DOURADO }} /> cota {cotaMensal != null ? brlMi(cotaMensal) : ""}
            </span>
          )}
        </div>
      )}
      <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ top: 28, right: marginRight, bottom: 10, left: 2 }}>
        <defs>
          <linearGradient id="fatAreaNavy" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={TEAL_SOFT} stopOpacity={0.35} />
            <stop offset="100%" stopColor={TEAL_SOFT} stopOpacity={0} />
          </linearGradient>
          <linearGradient id="fatLinhaNavy" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#006588" />
            <stop offset="100%" stopColor={NAVY_SOFT} />
          </linearGradient>
        </defs>
        {faixasNaoUteis.map((f, i) => (
          <ReferenceArea key={i} x1={f.ini} x2={f.fim} fill="#D7E6EE" fillOpacity={0.35} ifOverflow="hidden" />
        ))}
        <CartesianGrid stroke="#E8EEF2" vertical={false} />
        <XAxis dataKey="label" ticks={xTicks} tick={{ fontSize: 12, fill: "#7C8C99" }} axisLine={false} tickLine={false} />
        <YAxis
          tick={{ fontSize: 12, fill: "#7C8C99" }}
          axisLine={false} tickLine={false}
          ticks={yTicks} tickFormatter={fmtYMi} domain={[0, topoY]}
          width={isMobile ? 44 : 56}
        />
        <Tooltip content={<Tip />} cursor={{ stroke: "#B9DCE8", strokeDasharray: "3 3" }} />
        <Area type="monotone" dataKey="acumulado" stroke="url(#fatLinhaNavy)" strokeWidth={4} fill="url(#fatAreaNavy)" dot={false} activeDot={{ r: 5, fill: "#fff", stroke: NAVY_SOFT, strokeWidth: 3 }} connectNulls={false} isAnimationActive={false}>
          <LabelListFaturamento content={PontaHoje} />
        </Area>
        {temMeta && (
          <Line type="linear" dataKey="ritmo_meta" stroke={DOURADO} strokeWidth={2.2} strokeDasharray="5 5" dot={false} connectNulls isAnimationActive={false}>
            <LabelListFaturamento content={RotuloCota} />
          </Line>
        )}
        {hojeLabel && (
          /* UI-1 (item 7) — no celular, só a linha pontilhada vertical; o rótulo
             pequeno "HOJE" fica embaixo (abaixo do eixo X) para não encavalar. */
          <ReferenceLine
            x={hojeLabel} stroke={NAVY_SOFT} strokeDasharray="3 3" strokeOpacity={0.6}
            label={isMobile
              ? { value: "HOJE", position: "insideBottom", fill: NAVY_SOFT, fontSize: 10, fontWeight: 700, dy: 10 }
              : { value: "HOJE", position: "top", fill: NAVY_SOFT, fontSize: 10, fontWeight: 700, dy: -4 }}
          />
        )}
      </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
/* eslint-enable react/no-unstable-nested-components */

function TipChart({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg bg-white border border-black/10 shadow-md px-3 py-2 text-xs">
      <div className="font-semibold text-navy">{label}</div>
      <div className="text-slate-600 mt-1">Dia: <span className="font-semibold text-navy tabular-nums">{brl(p.valor_dia)}</span></div>
      <div className="text-slate-600">Acumulado: <span className="font-semibold text-navy tabular-nums">{brl(p.acumulado)}</span></div>
      {p.ritmo_meta != null && <div className="text-slate-600">Ritmo da cota real: <span className="font-semibold tabular-nums" style={{ color: BRAND_TEAL }}>{brl(p.ritmo_meta)}</span></div>}
    </div>
  );
}


function NivelTabela({ rows, totalLabel, extraLabel, totalEmpresa, semVinculo, showPctTotal }) {
  // Linha-base: nome + subtítulo (role/gerente), total, [venda própria], meta/%
  // + ação (seta para drill-down se onClick existir). Mobile: 390 px sem rolagem lateral.
  // FAT-1.1 (item 2): quando `showPctTotal` for true, a aba Vendedores ganha
  // a coluna "% do total" (vinda de `participacao_pct` do painel, sem recálculo).
  const soma = rows.reduce((a, r) => a + Number(r.total || 0), 0);
  const gridCols = showPctTotal
    ? "md:grid-cols-[1fr_140px_120px_120px_32px]"
    : "md:grid-cols-[1fr_160px_160px_120px_32px]";
  return (
    <div>
      <div className={`hidden md:grid ${gridCols} gap-3 px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground border-b border-black/5`}>
        <span>Nome</span>
        <span className="text-right">{totalLabel}</span>
        <span className="text-right">{showPctTotal ? "% do total" : (extraLabel || "")}</span>
        <span className="text-right">Meta</span>
        <span />
      </div>
      <ul className="divide-y divide-black/5">
        {rows.length === 0 && (
          <li className="py-6 text-center text-sm text-muted-foreground">Nenhuma linha com faturamento no período.</li>
        )}
        {rows.map((r) => (
          <li key={r.id} data-testid={r.testId}>
            <button
              onClick={r.onClick || undefined}
              disabled={!r.onClick}
              className={`w-full grid grid-cols-1 ${gridCols} gap-1 md:gap-3 items-center px-3 py-3 text-left ${r.onClick ? "hover:bg-slate-50 cursor-pointer" : "cursor-default"} min-w-0`}
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-navy truncate">{r.nome}</div>
                <div className="text-[11px] text-muted-foreground truncate">{r.sub}</div>
                <div className="md:hidden mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] tabular-nums">
                  <span className="font-semibold text-navy">{brl(r.total)}</span>
                  {showPctTotal && r.pctTotal != null && (
                    <span className="text-slate-500" data-testid={`${r.testId}-pct-total-m`}>{r.pctTotal}% do total</span>
                  )}
                  {!showPctTotal && extraLabel && r.extra != null && (
                    <span className="text-slate-500">{extraLabel}: {brl(r.extra)}</span>
                  )}
                  <span className="ml-auto"><MetaBadge tem={r.temMeta} pct={r.pct} /></span>
                </div>
              </div>
              <div className="hidden md:block text-sm font-semibold text-navy text-right tabular-nums">{brl(r.total)}</div>
              {showPctTotal ? (
                <div className="hidden md:block text-sm text-slate-500 text-right tabular-nums" data-testid={`${r.testId}-pct-total`}>
                  {r.pctTotal != null ? `${r.pctTotal}%` : "—"}
                </div>
              ) : (
                <div className="hidden md:block text-sm text-slate-500 text-right tabular-nums">{extraLabel && r.extra != null ? brl(r.extra) : "—"}</div>
              )}
              <div className="hidden md:block text-right"><MetaBadge tem={r.temMeta} pct={r.pct} /></div>
              <div className="hidden md:flex justify-end">
                {r.onClick && <ArrowRight className="w-4 h-4 text-slate-300" />}
              </div>
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-3 border-t border-black/5 pt-3 space-y-1">
        {semVinculo != null && semVinculo > 0 && (
          <div className="flex items-center justify-between text-xs" data-testid="fat-nivel-sem-vinculo">
            <span className="text-amber-700 font-medium">Vendas diretas MPires</span>
            <span className="font-semibold text-navy tabular-nums">{brl(semVinculo)}</span>
          </div>
        )}
        <div className="flex items-center justify-between text-sm">
          <span className="font-semibold text-navy">Total {semVinculo != null ? "da empresa" : "do grupo"}</span>
          <span className="font-semibold text-navy tabular-nums" data-testid="fat-nivel-total">
            {brl(semVinculo != null ? totalEmpresa : soma)}
          </span>
        </div>
      </div>
    </div>
  );
}
